import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/integrations/supabase/types";
import type { PaginationState } from "./usePagination";

type ProjectStatus = Database["public"]["Enums"]["project_status"];

export type ProjectStatusFilter = "active" | "completed" | "archived" | "all";

export const useProjects = (
  pagination?: PaginationState,
  statusFilter: ProjectStatusFilter = "active",
) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["projects", pagination?.page, statusFilter],
    queryFn: async () => {
      const applyStatus = (q: any): any => {
        if (statusFilter === "all") return q;
        if (statusFilter === "completed") return q.eq("status", "completed");
        if (statusFilter === "archived") return q.eq("status", "archived");
        // "active" = everything except archived
        return q.neq("status", "archived");
      };

      if (pagination) {
        let countQuery = supabase
          .from("projects")
          .select("id", { count: "exact", head: true });
        countQuery = applyStatus(countQuery as any);
        const { count } = await countQuery;
        pagination.setTotalCount(count ?? 0);
      }

      let query = supabase
        .from("projects")
        .select("*, project_types(name, abbreviation), customers(name)")
        .order("created_at", { ascending: false });
      query = applyStatus(query as any);

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
};

export const useProject = (id: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["projects", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select(
          "*, project_types(name, abbreviation), customers(name), project_assignments(user_id)"
        )
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!id,
  });
};

export const useCustomers = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("id, name")
        .eq("is_deleted", false)
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
};

export const useCreateProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      customer_id?: string | null;
      description?: string;
      status?: ProjectStatus;
    }) => {
      const { data: companyId, error: cidErr } = await supabase.rpc(
        "my_company_id"
      );
      if (cidErr) throw cidErr;

      const { data, error } = await supabase
        .from("projects")
        .insert({
          name: input.name,
          company_id: companyId,
          customer_id: input.customer_id || null,
          description: input.description || null,
          status: input.status || "active",
        })
        .select("id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["dashboard_metrics"] });
    },
  });
};

export const useUpdateProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Record<string, any>) => {
      const { id, ...rest } = input;
      const { error } = await supabase
        .from("projects")
        .update({ ...rest, updated_at: new Date().toISOString() } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["projects", vars.id] });
      qc.invalidateQueries({ queryKey: ["dashboard_metrics"] });
    },
  });
};

export const useDeleteProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) => {
      const { error } = await supabase.rpc("soft_delete_project", {
        p_project_id: projectId,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["dashboard_metrics"] });
    },
  });
};
